import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { errorCode } from "@/lib/auth";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { EMAIL_ERROR, isValidEmail } from "@/lib/validate";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Admin Sign In — Medical Insight" },
      { name: "description", content: "Sign in to manage the Medical Insight admin." },
    ],
  }),
  component: AuthPage,
});

type Step = "sign_in" | "new_password" | "forgot" | "reset";

// Cognito user pool policy (MiinewsAuthStack): minimum length only.
const MIN_PASSWORD_LENGTH = 8;

const NEUTRAL_CODE_SENT = "If an account exists for that email, we sent a code.";
const ADMIN_RESET_NOTICE = "Your administrator reset your password. Enter the code from your email.";
const PASSWORD_CHANGED = "Password changed. Sign in with your new password.";

/**
 * Reads the reset link the admin's email carries:
 *   https://www.miinews.com/auth#reset?email=<urlencoded>&code=<code>
 * Parsed by hand (not URLSearchParams) so a literal "+" in an address stays a "+".
 */
function readResetHash(): { email: string; code: string } | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash;
  if (!hash.startsWith("#reset")) return null;
  const out = { email: "", code: "" };
  const query = hash.slice("#reset".length).replace(/^\?/, "");
  for (const part of query.split("&")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    const key = part.slice(0, i);
    let value = part.slice(i + 1);
    try {
      value = decodeURIComponent(value);
    } catch {
      // keep the raw value
    }
    if (key === "email") out.email = value.trim();
    else if (key === "code") out.code = value.trim();
  }
  return out;
}

/** Plain-language text for Cognito errors on the forgot/reset steps. */
function resetErrorMessage(err: unknown, fallback: string): string {
  switch (errorCode(err)) {
    case "CodeMismatchException":
    case "UserNotFoundException":
      return "That code is not right.";
    case "ExpiredCodeException":
      return "That code has expired. Send a new code.";
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return "Too many attempts. Wait a few minutes and try again.";
    default:
      return err instanceof Error && err.message ? err.message : fallback;
  }
}

function AuthPage() {
  const navigate = useNavigate();
  const { user, loading, signIn, completeNewPassword, forgotPassword, confirmForgotPassword, signOut } =
    useAuth();
  // A reset link opens the reset step directly, prefilled.
  const [initialReset] = useState(readResetHash);
  const [step, setStep] = useState<Step>(initialReset ? "reset" : "sign_in");
  const [email, setEmail] = useState(initialReset?.email ?? "");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState(initialReset?.code ?? "");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  // Informational line shown at the top of the current step's card.
  const [notice, setNotice] = useState<string | null>(null);
  // Error from the server for the forgot/reset steps.
  const [formError, setFormError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Take the code out of the address bar (and history) as soon as it is read.
  useEffect(() => {
    if (!initialReset) return;
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, "", pathname + search);
  }, [initialReset]);

  // Already signed in — go straight to the admin (but never out of a reset in progress).
  useEffect(() => {
    if (!loading && user && step === "sign_in") navigate({ to: "/admin" });
  }, [loading, user, step, navigate]);

  const goTo = (next: Step, message: string | null = null) => {
    setStep(next);
    setNotice(message);
    setFormError(null);
    setEmailError(null);
    setCodeError(null);
    setPasswordError(null);
    setConfirmError(null);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setBusy(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result.status === "new_password_required") {
        goTo("new_password");
        toast.info("Please set a new password to finish signing in.");
      } else if (result.status === "reset_required") {
        setPassword("");
        setCode("");
        setNewPassword("");
        setConfirmPassword("");
        goTo("reset", ADMIN_RESET_NOTICE);
      } else {
        toast.success("Welcome");
        navigate({ to: "/admin" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await completeNewPassword(newPassword);
      toast.success("Password updated — welcome");
      navigate({ to: "/admin" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not set the new password");
    } finally {
      setBusy(false);
    }
  };

  // Always answers with the same neutral message, so the form never reveals
  // whether an account exists.
  const sendCode = async () => {
    try {
      await forgotPassword(email.trim());
    } catch {
      // deliberately ignored
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setBusy(true);
    await sendCode();
    setBusy(false);
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    goTo("reset", NEUTRAL_CODE_SENT);
  };

  const handleResendCode = async () => {
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setResending(true);
    await sendCode();
    setResending(false);
    setFormError(null);
    setNotice(NEUTRAL_CODE_SENT);
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const emailBad = !isValidEmail(email);
    const codeBad = !code.trim();
    const passwordBad = newPassword.length < MIN_PASSWORD_LENGTH;
    const confirmBad = !passwordBad && newPassword !== confirmPassword;
    setEmailError(emailBad ? EMAIL_ERROR : null);
    setCodeError(codeBad ? "Enter the code from your email." : null);
    setPasswordError(passwordBad ? `Use at least ${MIN_PASSWORD_LENGTH} characters.` : null);
    setConfirmError(confirmBad ? "The two passwords do not match." : null);
    if (emailBad || codeBad || passwordBad || confirmBad) return;

    setBusy(true);
    try {
      await confirmForgotPassword(email.trim(), code.trim(), newPassword);
      // Drop any session left in this browser; they sign in fresh.
      signOut();
      setPassword("");
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      goTo("sign_in", PASSWORD_CHANGED);
    } catch (err) {
      setFormError(resetErrorMessage(err, "Could not reset the password. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const linkClass = "text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline";

  const noticeLine = notice && (
    <p role="status" className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
      {notice}
    </p>
  );

  const emailField = (id: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Email</Label>
      <Input
        id={id}
        type="email"
        required
        autoComplete={step === "reset" ? "username" : "email"}
        value={email}
        aria-invalid={emailError ? true : undefined}
        aria-describedby={emailError ? `${id}-error` : undefined}
        onChange={(e) => {
          setEmail(e.target.value);
          if (emailError) setEmailError(null);
        }}
      />
      {emailError && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {emailError}
        </p>
      )}
    </div>
  );

  if (step === "new_password") {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
            <CardDescription>
              Your account was created with a temporary password. Choose a new one to
              finish signing in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form className="space-y-3" onSubmit={handleNewPassword}>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Saving…" : "Set password and sign in"}
              </Button>
            </form>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => goTo("sign_in")}
            >
              Back to sign in
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === "forgot") {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card>
          <CardHeader>
            <CardTitle>Reset your password</CardTitle>
            <CardDescription>
              Enter your email and we will send you a code to choose a new password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {noticeLine}
            <form className="space-y-3" noValidate onSubmit={handleForgot}>
              {emailField("forgot-email")}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Sending…" : "Send code"}
              </Button>
            </form>
            <div className="flex items-center justify-between">
              <button type="button" className={linkClass} onClick={() => goTo("sign_in")}>
                Back to sign in
              </button>
              <button type="button" className={linkClass} onClick={() => goTo("reset")}>
                I already have a code
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === "reset") {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card>
          <CardHeader>
            <CardTitle>Choose a new password</CardTitle>
            <CardDescription>
              Enter the code from your email and pick a new password of at least{" "}
              {MIN_PASSWORD_LENGTH} characters.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {noticeLine}
            <form className="space-y-3" noValidate onSubmit={handleReset}>
              {emailField("reset-email")}
              <div className="space-y-1.5">
                <Label htmlFor="reset-code">Code</Label>
                <Input
                  id="reset-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  aria-invalid={codeError ? true : undefined}
                  aria-describedby={codeError ? "reset-code-error" : undefined}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (codeError) setCodeError(null);
                  }}
                />
                {codeError && (
                  <p id="reset-code-error" className="text-xs text-destructive">
                    {codeError}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reset-new-password">New password</Label>
                <Input
                  id="reset-new-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={newPassword}
                  aria-invalid={passwordError ? true : undefined}
                  aria-describedby={passwordError ? "reset-new-password-error" : undefined}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                />
                {passwordError && (
                  <p id="reset-new-password-error" className="text-xs text-destructive">
                    {passwordError}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reset-confirm-password">Confirm new password</Label>
                <Input
                  id="reset-confirm-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  aria-invalid={confirmError ? true : undefined}
                  aria-describedby={confirmError ? "reset-confirm-password-error" : undefined}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (confirmError) setConfirmError(null);
                  }}
                />
                {confirmError && (
                  <p id="reset-confirm-password-error" className="text-xs text-destructive">
                    {confirmError}
                  </p>
                )}
              </div>
              {formError && (
                <p role="alert" className="text-sm text-destructive">
                  {formError}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Saving…" : "Reset password"}
              </Button>
            </form>
            <div className="flex items-center justify-between">
              <button type="button" className={linkClass} onClick={() => goTo("sign_in")}>
                Back to sign in
              </button>
              <button
                type="button"
                className={linkClass}
                disabled={resending}
                onClick={() => void handleResendCode()}
              >
                {resending ? "Sending…" : "Send a new code"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <Card>
        <CardHeader>
          <CardTitle>Admin sign in</CardTitle>
          <CardDescription>
            Access is invite-only. Ask an administrator if you need an account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {noticeLine}
          <form className="space-y-3" noValidate onSubmit={handleSignIn}>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? "auth-email-error" : undefined}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
              />
              {emailError && (
                <p id="auth-email-error" className="text-xs text-destructive">
                  {emailError}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <button type="button" className={linkClass} onClick={() => goTo("forgot")}>
            Forgot password?
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
